import { type ReactNode } from 'react';
import { cn } from './cn';

export interface SegmentedItem {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps {
  items: SegmentedItem[];
  /** Currently selected segment id (controlled). */
  value: string;
  onChange: (id: string) => void;
  size?: 'sm' | 'md';
  className?: string;
  /** Accessible group label (recommended when there is no visible label). */
  'aria-label'?: string;
}

const SIZES: Record<NonNullable<SegmentedControlProps['size']>, string> = {
  sm: 'px-2.5 py-1 text-xs gap-1.5',
  md: 'px-3 py-1.5 text-sm gap-1.5',
};

/**
 * A single-select segmented toggle — the shared control for the app's various
 * hand-rolled two/three-way toggles (sprint board view, chat grounding, etc.).
 * Visually consistent with {@link Tabs} (same container + active surface), but
 * uses `aria-pressed` button semantics since it toggles a mode rather than
 * switching tab panels.
 */
export function SegmentedControl({
  items,
  value,
  onChange,
  size = 'md',
  className,
  'aria-label': ariaLabel,
}: SegmentedControlProps) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center gap-1 rounded-xl border border-surface-border bg-surface-1 p-1',
        className,
      )}
    >
      {items.map((item) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            aria-pressed={active}
            disabled={item.disabled}
            onClick={() => onChange(item.id)}
            className={cn(
              'inline-flex items-center rounded-lg font-medium transition-colors',
              'disabled:cursor-not-allowed disabled:opacity-50',
              SIZES[size],
              active
                ? 'bg-surface-3 text-zinc-100 shadow-sm'
                : 'text-zinc-400 hover:text-zinc-200',
            )}
          >
            {item.icon}
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
